import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Pencil } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import SurfLevelSelector from './SurfLevelSelector';
import { nextSurfLevel, mandatoryCountForLevel, type SurfLevel } from '@/lib/surf-progression';

interface SurfProfileCardProps {
  displayName: string;
  level: SurfLevel;
  /** Count of this level's achievements the user has completed -- real, from local progression state. */
  levelAchievedCount: number;
  /** Omitted entirely in read-only previews (e.g. the dev harness's non-interactive
   * demo) -- when present, shows the "Change Level" entry point. */
  onChangeLevel?: (level: SurfLevel) => void;
}

/**
 * The hero component of My Swell Breaks -- surfing identity and progression
 * only. Travel stats (countries/destinations) were removed from here: Surf
 * Passport immediately below already owns that number, and showing it twice
 * read as redundant rather than reinforcing.
 */
const SurfProfileCard = ({ displayName, level, levelAchievedCount, onChangeLevel }: SurfProfileCardProps) => {
  const mandatoryCount = mandatoryCountForLevel(level);
  const next = nextSurfLevel(level);
  const targetPct = mandatoryCount > 0 ? Math.round((levelAchievedCount / mandatoryCount) * 100) : 0;
  const remaining = Math.max(mandatoryCount - levelAchievedCount, 0);
  const [changeOpen, setChangeOpen] = useState(false);

  // Bar fills from 0 on mount rather than snapping straight to its value --
  // Progress's own indicator already has `transition-all`, so animating the
  // value prop is enough, no extra animation code needed.
  const [displayPct, setDisplayPct] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDisplayPct(targetPct), 80);
    return () => clearTimeout(t);
  }, [targetPct]);

  return (
    <div className="rounded-2xl border border-border/50 bg-card px-5 py-5 sm:px-7 sm:py-6">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">My Surf Profile</p>
      <h2 className="mt-0.5 font-serif text-xl sm:text-2xl font-semibold text-foreground">{displayName}</h2>

      <div className="mt-4 flex items-baseline justify-between gap-3">
        <span className="font-serif text-2xl sm:text-3xl text-primary leading-none">{level}</span>
        {next && <span className="text-xs sm:text-sm text-muted-foreground">Progress to {next}</span>}
      </div>

      <div className="mt-3">
        <Progress value={displayPct} className="h-2" />
        <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
          <span>{levelAchievedCount} / {mandatoryCount} {level} achievements</span>
          {next && remaining > 0 && <span>{remaining} to {next}</span>}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button asChild variant="default" size="sm" className="gap-1.5 group">
          <Link to="/my-swell-breaks/progression">
            View My Surfing
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
        {onChangeLevel && (
          <button
            type="button"
            onClick={() => setChangeOpen(true)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3 w-3" />
            Change Level
          </button>
        )}
      </div>

      {onChangeLevel && (
        <Sheet open={changeOpen} onOpenChange={setChangeOpen}>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
            <SheetHeader className="text-left">
              <SheetTitle className="font-serif">Change your surfing level</SheetTitle>
              <SheetDescription>
                {/* Prototype-safe today: nothing about lower-level "prerequisite complete"
                    status is stored data, it's derived from the current level (see
                    isPrerequisiteLevel in surf-progression.ts), so changing level here
                    can't delete or corrupt anything. A real backend implementation needs
                    its own rules first -- see docs/engineering/my-swell-breaks-v1-prototype.md. */}
                This won't affect achievements you've already logged.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              <SurfLevelSelector
                value={level}
                onSelect={(next) => {
                  onChangeLevel(next);
                  setChangeOpen(false);
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default SurfProfileCard;
